package com.bikompanion.ui.composables

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.bikompanion.ui.preview.PreviewSurface
import com.bikompanion.ui.strings.LocalStrings

/**
 * Says the racks couldn't be loaded, and offers to try again.
 *
 * Racks normally come from the dataset shipped with the app and cannot fail, so this is for the
 * fallback path — a build whose dataset will not open, left asking public Overpass mirrors that
 * refuse a caller for minutes at a time. Until this existed the only sign of that was the spinner
 * disappearing over an empty map, which reads as "there is nowhere to lock up around here" — the
 * one thing a failure must never be mistaken for. The retry matters too: a failed area backs off
 * for a while (`ParkingRetryBackoff`), so without a way to ask again the rider would just wait.
 */
@Composable
internal fun ParkingLoadFailedBanner(
    isVisible: Boolean,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    if (!isVisible) return
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.92f),
        contentColor = MaterialTheme.colorScheme.onErrorContainer,
        shadowElevation = 2.dp,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier.padding(start = 12.dp, end = 4.dp, top = 2.dp, bottom = 2.dp),
        ) {
            Text(
                text = LocalStrings.current.parkingLoadFailed,
                style = MaterialTheme.typography.labelMedium,
            )
            TextButton(onClick = onRetry) {
                Text(text = LocalStrings.current.actionRetry, style = MaterialTheme.typography.labelMedium)
            }
        }
    }
}

@Preview
@Composable
private fun ParkingLoadFailedBannerPreview() {
    PreviewSurface {
        ParkingLoadFailedBanner(isVisible = true, onRetry = {}, modifier = Modifier.padding(16.dp))
    }
}
